import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCancelToken } from 'app/services/api/hooks';
import { ModalName } from 'app/enums';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { Modal } from 'shared/components/Modal';
import { useStyles } from '../PaymentModal.styles';
import { Box, Typography } from '@mui/material';
import { AvailableBalance } from '../AvailableBalance';
import { useDispatch } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { modalsReducers } from 'app/store/modal/slice';
import { ProviderOptions } from '../ProviderOptions';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { getWithdrawalQuotas } from 'modules/payments/api/get-withdrawal-quotas';
import { UserQuotaStatusDto } from 'app/interfaces/dto/user-quota-status.dto';
import { WITHDRAW_FUNDS_OPTIONS } from 'modules/payments/constants';
import { WithdrawCryptoContent } from './WithdrawCryptoContent';
import { WithdrawFundsContentRef } from './withdraw-funds-ref';
import { WithdrawRequestContent } from './WithdrawRequestContent';
import { WithdrawOption } from 'modules/payments/enums';

const WithdrawFundsModalComponent: FC = () => {
  const cancelToken = useCancelToken();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const contentRef = useRef<WithdrawFundsContentRef | null>(null);

  const [isLoading, setLoading] = useState(false);
  const [isValid, setValid] = useState(false);
  const [quotas, setQuotas] = useState<UserQuotaStatusDto | undefined>();

  const [selectedWithdrawOption, setWithdrawOption] = useState(WITHDRAW_FUNDS_OPTIONS[0].value);

  const loadQuotas = useCallback(() => {
    if (!MultiCurrencyConfig.isUserQuotaWithdrawalEnabled()) return;

    setLoading(true);
    getWithdrawalQuotas({ cancelToken })
      .then((data) => setQuotas(data))
      .finally(() => setLoading(false));
  }, [cancelToken]);

  useEffect(() => loadQuotas(), [loadQuotas]);

  const onSuccessSubmit = useCallback(() => {
    dispatch(modalsReducers.closeModal(ModalName.BalanceWithdrawFunds));

    dispatch(
      modalsReducers.openModal({
        name: ModalName.BalanceWithdrawFundsConfirmed,
        data: { withdrawOption: selectedWithdrawOption },
      })
    );
  }, [dispatch, selectedWithdrawOption]);

  const handleSubmit = useCallback(() => {
    const submit = contentRef?.current?.handleSubmit;

    if (submit && typeof submit === 'function') {
      submit();
    }
  }, []);

  const actionButtonText = useMemo(() => {
    switch (selectedWithdrawOption) {
      case WithdrawOption.Crypto:
        return 'Withdraw funds to External Crypto Wallet';
      case WithdrawOption.PaypalRequest:
        return 'Create PayPal Withdraw Request';
      default:
        return null;
    }
  }, [selectedWithdrawOption]);

  return (
    <Modal
      name={ModalName.BalanceWithdrawFunds}
      title="Withdraw Funds"
      maxWidth="md"
      contentHeight={800}
      withScroll
      actions={
        <LoaderButton
          className={classes.button}
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          disabled={!isValid || isLoading}
          loading={isLoading}
          onClick={handleSubmit}
        >
          {actionButtonText}
        </LoaderButton>
      }
    >
      <Box className={classes.root}>
        <AvailableBalance onlyActive={false} />

        <Box mb={1}>
          <Typography mb={2} variant="h6">
            Select a withdrawal option:
          </Typography>

          <Box mb={2}>
            <ProviderOptions
              options={WITHDRAW_FUNDS_OPTIONS}
              selected={selectedWithdrawOption}
              onChange={(value: string) => setWithdrawOption(value as WithdrawOption)}
              isDisabled={isLoading}
            />
          </Box>
        </Box>

        {selectedWithdrawOption === WithdrawOption.Crypto ? (
          <WithdrawCryptoContent
            isLoading={isLoading}
            setLoading={setLoading}
            onValidChange={setValid}
            quotas={quotas}
            onSuccessSubmit={onSuccessSubmit}
            ref={contentRef}
          />
        ) : null}

        {selectedWithdrawOption === WithdrawOption.PaypalRequest ? (
          <WithdrawRequestContent
            isLoading={isLoading}
            setLoading={setLoading}
            onValidChange={setValid}
            quotas={quotas}
            onSuccessSubmit={onSuccessSubmit}
            ref={contentRef}
          />
        ) : null}
      </Box>
    </Modal>
  );
};

export const WithdrawFundsModal = withModalOpenChecker(
  WithdrawFundsModalComponent,
  ModalName.BalanceWithdrawFunds
);
